import axios from 'axios';
import { auth } from '../config/firebase.config';

// Automatically detect environment and set API URL
const getApiUrl = () => {
    // If environment variable is set, use it
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }

    // Check if running on localhost (development)
    const isLocalhost = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '';

    if (isLocalhost) {
        console.log('Running in local development mode - using localhost backend');
        return 'http://localhost:5000/api';
    } else {
        console.log('Running in production mode - using Render backend');
        return 'https://trf-p6aq.onrender.com/api';
    }
};

const API_URL = getApiUrl();

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add Firebase ID token to requests
api.interceptors.request.use(
    async (config) => {
        try {
            if (auth.currentUser) {
                const token = await auth.currentUser.getIdToken();
                config.headers.Authorization = `Bearer ${token}`;
            }
        } catch (error) {
            console.error('Error getting ID token:', error);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid, try to refresh
            try {
                if (auth.currentUser) {
                    const newToken = await auth.currentUser.getIdToken(true);
                    error.config.headers.Authorization = `Bearer ${newToken}`;
                    return axios(error.config);
                }
            } catch (refreshError) {
                // Refresh failed, logout user
                await auth.signOut();
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API - Note: Login is handled by Firebase Auth in AuthContext
export const authAPI = {
    // These endpoints are not needed with Firebase Auth
    // login and register are handled client-side
};

// Scholar API
export const scholarAPI = {
    getApplications: () => api.get('/scholar/applications'),
    createApplication: (data) => api.post('/scholar/applications', data),
    getClaims: () => api.get('/scholar/claims'),
    submitClaim: (data) => api.post('/scholar/claims', data),
};

// Scholar Portal API (TRF-SCHOLAR equivalent)
export const scholarPortalAPI = {
    // Profile
    getProfile: () => api.get('/scholar-portal/profile'),
    // Claims
    getClaimStatus: (month, year) => api.get(`/scholar-portal/claims/status?month=${month}&year=${year}`),
    submitClaim: (data) => api.post('/scholar-portal/claims', data),
    // Publications
    getMyPublications: () => api.get('/scholar-portal/publications/my-publications'),
    getPublicationStats: () => api.get('/scholar-portal/publications/stats/my-stats'),
    getComplianceCheck: () => api.get('/scholar-portal/publications/compliance/check'),
    addPublication: (data) => api.post('/scholar-portal/publications', data),
    updatePublication: (id, data) => api.put(`/scholar-portal/publications/${id}`, data),
    deletePublication: (id) => api.delete(`/scholar-portal/publications/${id}`),
    // Bank Details
    getBankDetails: () => api.get('/scholar-portal/bank-details'),
    updateBankDetails: (data) => api.post('/scholar-portal/bank-details', data),
    // Balance CL
    getBalCL: () => api.get('/scholar-portal/bal-cl'),
    updateBalCL: (usedCl) => api.post('/scholar-portal/bal-cl/update', { usedCl }),
};

// Supervisor API
export const supervisorAPI = {
    getClaims: () => api.get('/supervisor/claims'),
    getClaimDetails: (id) => api.get(`/supervisor/claims/${id}`),
    approveClaim: (id, data) => api.post(`/supervisor/claims/${id}/approve`, data),
    rejectClaim: (id, reason, data) => api.post(`/supervisor/claims/${id}/reject`, { reason, ...data }),
    getScholars: () => api.get('/supervisor/scholars'),
};

// DLC API
export const dlcAPI = {
    getClaims: () => api.get('/dlc/claims'),
    getClaimDetails: (id) => api.get(`/dlc/claims/${id}`),
    approveClaim: (id) => api.post(`/dlc/claims/${id}/approve`),
    rejectClaim: (id, reason) => api.post(`/dlc/claims/${id}/reject`, { reason }),
    getScholars: () => api.get('/dlc/scholars'),
};

// HOD API
export const hodAPI = {
    getClaims: () => api.get('/hod/claims'),
    getClaimDetails: (id) => api.get(`/hod/claims/${id}`),
    approveClaim: (id, data) => api.post(`/hod/claims/${id}/approve`, data),
    rejectClaim: (id, reason, data) => api.post(`/hod/claims/${id}/reject`, { reason, ...data }),
    getScholars: () => api.get('/hod/scholars'),
};

// Admin API
export const adminAPI = {
    getUsers: () => api.get('/admin/users'),
    createUser: (data) => api.post('/admin/users', data),
    enrollScholar: (data) => api.post('/admin/scholars', data),
    getClaims: (params) => api.get('/admin/claims', { params }),
    getClaimDetails: (id) => api.get(`/admin/claims/${id}`),
    approveClaim: (id) => api.post(`/admin/claims/${id}/approve`),
    rejectClaim: (id, reason) => api.post(`/admin/claims/${id}/reject`, { reason }),
    bulkApproveClaims: (claimIds) => api.post('/admin/claims/bulk-approve', { claimIds }),
    reviewClaim: (id, data) => api.post(`/admin/claims/${id}/review`, data),
    processPayment: (id) => api.post(`/admin/claims/${id}/payment`),
    getStipendConfig: () => api.get('/admin/stipend-config'),
    setStipendConfig: (data) => api.post('/admin/stipend-config', data),
    // Application approval (formerly dean)
    getApplicationsForApproval: () => api.get('/admin/applications/pending-approval'),
    getApplicationStats: () => api.get('/admin/applications/stats'),
    reviewApplication: (id, data) => api.post(`/admin/applications/${id}/review`, data),
    suspendFellowship: (id, reason) => api.post(`/admin/scholars/${id}/suspend`, { reason }),
    discontinueFellowship: (id, reason) => api.post(`/admin/scholars/${id}/discontinue`, { reason }),
};

// Application API
export const applicationAPI = {
    getById: (id) => api.get(`/applications/${id}`),
    getDocuments: (id) => api.get(`/applications/${id}/documents`),
    uploadDocument: (id, formData) => {
        return api.post(`/applications/${id}/documents`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getReviews: (id) => api.get(`/applications/${id}/reviews`),
};

// Notification API
export const notificationAPI = {
    getAll: () => api.get('/notifications'),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    markAsRead: (id) => api.put(`/notifications/${id}/read`),
    markAllAsRead: () => api.put('/notifications/read-all'),
};

export default api;
